import { useCallback, useState } from 'react';
import {
  buildTaskTree,
  flattenTaskTree,
  findPreviousSibling,
  getEffectivePrioritizeSection
} from '../utils/taskUtils';
import {
  isSequentialTask,
  getSequenceNumber,
  renumberSequentialListAroundTask,
  renumberSequentialListAfterIndent
} from '../utils/sequentialTasks';
import { getCurrentCaretPosition, setCaretToEnd } from '../../../shared/utils/caretPositioning';

/**
 * Hook for handling keyboard navigation in task text
 * @param {Object} params - Configuration object
 * @param {Array} params.tasks - All tasks
 * @param {Set} params.expandedTasks - Set of expanded task IDs
 * @param {Function} params.setExpandedTasks - Function to set expanded tasks
 * @param {Function} params.setEditingTaskId - Function to set editing task ID
 * @param {Object} params.editingElementRef - Ref to the editing element
 * @param {Function} params.updateTaskText - Function to update task text
 * @param {Function} params.updateTaskStatus - Function to update task status
 * @param {Function} params.updateTaskOrder - Function to update a single task order
 * @param {Function} params.updateTaskOrders - Function to batch update task orders
 * @param {Function} params.updateTaskParentWithTextAndOrder - Function to update task parent and order
 * @param {Function} params.createTask - Function to create a new task
 * @param {Function} params.deleteTask - Function to delete a task
 * @param {Object} params.caretAtZeroTaskIdsRef - Ref to track tasks with caret at zero
 * @param {Function} params.focusFileName - Function to focus file name
 * @param {Object} params.arrowKeyCaretPositionRef - Ref for arrow key caret positioning
 * @param {Object} params.tabCaretPositionRef - Ref for tab caret positioning
 * @param {Object} params.pendingFocusRef - Ref for pending focus (set by hook, consumed by parent effect)
 * @param {Object} params.placeholderStatusRef - Ref to placeholder checkbox status (when no tasks)
 * @param {Object} params.placeholderDueDateRef - Ref to placeholder due date (when no tasks)
 * @param {Object} params.placeholderIsLaterDateRef - Ref to whether placeholder due is "Later" (when no tasks)
 */
export function useTaskKeyboardNavigation({
  tasks,
  expandedTasks,
  setExpandedTasks,
  setEditingTaskId,
  editingElementRef,
  updateTaskText,
  updateTaskStatus,
  updateTaskOrder,
  updateTaskOrders,
  updateTaskParentWithTextAndOrder,
  createTask,
  deleteTask,
  caretAtZeroTaskIdsRef,
  focusFileName,
  arrowKeyCaretPositionRef,
  tabCaretPositionRef,
  pendingFocusRef,
  placeholderStatusRef,
  placeholderDueDateRef,
  placeholderIsLaterDateRef,
  filter,
  visiblePrioritizeSections
}) {
  const [focusRequestId, setFocusRequestId] = useState(0);

  const tryFocusPending = useCallback(() => {
    if (!pendingFocusRef?.current) return;
    const pending = pendingFocusRef.current;
    const el = document.querySelector(`.task-text[data-react-key="${pending.reactKey}"]`);
    if (!el) return;
    el.focus();
    if (pending.runFocus) {
      pending.runFocus(el);
    } else {
      setCaretToEnd(el);
    }
    pendingFocusRef.current = null;
  }, [pendingFocusRef]);

  const handleTaskTextKeyDown = useCallback(async (e, clientId) => {
    const isMobile = typeof navigator !== 'undefined' && (navigator.maxTouchPoints > 0 || 'ontouchstart' in window);

    if (e.key === 'Backspace') {
      const currentText = e.target.textContent || e.target.innerText || '';
      const trimmedText = currentText.trim();
      
      // Handle placeholder task - don't delete, just keep empty
      if (clientId === 'placeholder' || clientId === 'placeholder_client_key') {
        const content = e.target.textContent || '';
        if (content.length === 0) {
          e.preventDefault();
          e.target.textContent = '';
          e.target.classList.add('task-text-placeholder-empty');
        }
        return;
      }
      
      // Find task by clientId
      const currentTask = tasks.find(t => t._reactKey === clientId);
      if (!currentTask) return;
      const isPrioritizeView = filter === 'prioritize';
      const getTodayDate = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      const getLaterDate = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const randomDays = Math.floor(Math.random() * 8) + 7;
        const laterDate = new Date(today);
        laterDate.setDate(today.getDate() + randomDays);
        const dayOfWeek = laterDate.getDay();
        if (dayOfWeek === 0) laterDate.setDate(laterDate.getDate() + 1);
        if (dayOfWeek === 6) laterDate.setDate(laterDate.getDate() + 2);
        const year = laterDate.getFullYear();
        const month = String(laterDate.getMonth() + 1).padStart(2, '0');
        const day = String(laterDate.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      const getCreateDefaultsForCurrentTask = () => {
        if (!isPrioritizeView) {
          return { section: 'no-due-date', status: 'incomplete', dueDate: null, isLaterDate: false };
        }
        const section = getEffectivePrioritizeSection(currentTask, visiblePrioritizeSections);
        if (section === 'today') {
          return { section, status: 'incomplete', dueDate: getTodayDate(), isLaterDate: false };
        }
        if (section === 'later') {
          return { section, status: 'incomplete', dueDate: getLaterDate(), isLaterDate: true };
        }
        if (section === 'work-in-progress') {
          return { section, status: 'wip', dueDate: null, isLaterDate: false };
        }
        if (section === 'done') {
          return { section, status: 'complete', dueDate: null, isLaterDate: false };
        }
        return { section: 'no-due-date', status: 'incomplete', dueDate: null, isLaterDate: false };
      };
      
      // Check caret position
      const caretPosition = getCurrentCaretPosition(e.target);
      
      // If task has no text
      if (!trimmedText || trimmedText.length === 0) {
        e.preventDefault();
        // On mobile: subtask with no text → Backspace = Shift+Tab (un-indent), not delete
        if (isMobile && currentTask.parent_task_id) {
          return handleTaskTextKeyDown(Object.assign({}, e, { key: 'Tab', shiftKey: true, preventDefault: () => {} }), clientId);
        }
        await deleteTask(currentTask.id); // Use database ID
        return;
      }
      
      // If caret is at position 0 and task has text
      if (caretPosition === 0) {
        // On mobile: if task is a subtask, Backspace at 0 = Shift+Tab (un-indent)
        if (isMobile && currentTask.parent_task_id) {
          e.preventDefault();
          return handleTaskTextKeyDown(Object.assign({}, e, { key: 'Tab', shiftKey: true, preventDefault: () => {} }), clientId);
        }

        e.preventDefault();
        
        // Find previous task in flattened list
        const taskTree = buildTaskTree(tasks);
        const flattenedTasks = flattenTaskTree(taskTree, expandedTasks);
        const currentIndex = flattenedTasks.findIndex(t => t._reactKey === clientId);
        
        if (currentIndex > 0) {
          const previousTask = flattenedTasks[currentIndex - 1];
          const previousTaskText = previousTask.text || '';
          const currentTaskText = currentText;
          
          // Append current task text to previous task
          const newPreviousTaskText = previousTaskText + currentTaskText;
          
          // Update previous task text
          await updateTaskText(previousTask.id, newPreviousTaskText); // Use database ID
          
          // Delete current task
          await deleteTask(currentTask.id); // Use database ID
          
          // Move focus to previous task and position caret right before appended text
          setEditingTaskId(previousTask._reactKey); // Use clientId
          
          // Store caret positioning data for useLayoutEffect to handle after React renders
          arrowKeyCaretPositionRef.current = {
            taskId: previousTask._reactKey, // Use clientId
            targetPosition: previousTaskText.length
          };
        }
        return;
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      
      // Handle placeholder task - create real task when user presses Enter
      if (clientId === 'placeholder' || clientId === 'placeholder_client_key') {
        const currentText = e.target.textContent || e.target.innerText;
        const trimmedText = currentText.trim();
        const placeholderStatus = placeholderStatusRef?.current ?? 'incomplete';
        const placeholderDueDate = placeholderDueDateRef?.current ?? null;
        if (trimmedText) {
          // Clear placeholder editing state first
          setEditingTaskId(null);
          
          // Calculate order for the first task - should be 1 if no tasks exist
          const rootTasks = tasks.filter(t => t.parent_task_id === null);
          const maxOrder = rootTasks.length > 0
            ? Math.max(...rootTasks.map(t => t.order !== null && t.order !== undefined ? t.order : 0))
            : 0;
          const newOrder = maxOrder + 1;
          const placeholderIsLater =
            placeholderIsLaterDateRef?.current === true;
          const newTask = createTask(
            trimmedText,
            null,
            newOrder,
            placeholderStatus,
            placeholderDueDate,
            null,
            placeholderIsLater
          );
          if (newTask) {
            // Create empty task below so user can continue typing (matches normal Enter behavior)
            const emptyTask = createTask('', null, newOrder + 1, 'incomplete', null, null);
            if (emptyTask) {
              if (pendingFocusRef) pendingFocusRef.current = { reactKey: emptyTask._reactKey };
              setEditingTaskId(emptyTask._reactKey);
            } else {
              if (pendingFocusRef) pendingFocusRef.current = { reactKey: newTask._reactKey };
              setEditingTaskId(newTask._reactKey);
            }
          }
        } else {
          // Empty placeholder + Enter: convert placeholder to empty task, add new task below, focus new task
          setEditingTaskId(null);
          const rootTasks = tasks.filter(t => t.parent_task_id === null);
          const maxOrder = rootTasks.length > 0
            ? Math.max(...rootTasks.map(t => t.order !== null && t.order !== undefined ? t.order : 0))
            : 0;
          const firstOrder = maxOrder + 1;
          const placeholderIsLaterEmpty =
            placeholderIsLaterDateRef?.current === true;
          const emptyTask = createTask(
            '',
            null,
            firstOrder,
            placeholderStatus,
            placeholderDueDate,
            null,
            placeholderIsLaterEmpty
          );
          if (emptyTask) {
            const newTaskBelow = createTask('', null, firstOrder + 1, 'incomplete', null, null);
            if (newTaskBelow) {
              if (pendingFocusRef) pendingFocusRef.current = { reactKey: newTaskBelow._reactKey };
              setEditingTaskId(newTaskBelow._reactKey);
            } else {
              if (pendingFocusRef) pendingFocusRef.current = { reactKey: emptyTask._reactKey };
              setEditingTaskId(emptyTask._reactKey);
            }
          }
        }
        return;
      }
      
      const currentTask = tasks.find(t => t._reactKey === clientId);
      if (!currentTask) return;
      const isPrioritizeView = filter === 'prioritize';
      const getTodayDate = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      const getLaterDate = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const randomDays = Math.floor(Math.random() * 8) + 7;
        const laterDate = new Date(today);
        laterDate.setDate(today.getDate() + randomDays);
        const dayOfWeek = laterDate.getDay();
        if (dayOfWeek === 0) laterDate.setDate(laterDate.getDate() + 1);
        if (dayOfWeek === 6) laterDate.setDate(laterDate.getDate() + 2);
        const year = laterDate.getFullYear();
        const month = String(laterDate.getMonth() + 1).padStart(2, '0');
        const day = String(laterDate.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      const getCreateDefaultsForCurrentTask = () => {
        if (!isPrioritizeView) {
          return { status: 'incomplete', dueDate: null, isLaterDate: false, prioritizeSection: null };
        }
        const prioritizeSection = getEffectivePrioritizeSection(
          currentTask,
          visiblePrioritizeSections
        );
        if (prioritizeSection === 'today') {
          return {
            status: 'incomplete',
            dueDate: getTodayDate(),
            isLaterDate: false,
            prioritizeSection
          };
        }
        if (prioritizeSection === 'later') {
          return {
            status: 'incomplete',
            dueDate: getLaterDate(),
            isLaterDate: true,
            prioritizeSection
          };
        }
        if (prioritizeSection === 'work-in-progress') {
          return {
            status: 'wip',
            dueDate: null,
            isLaterDate: false,
            prioritizeSection
          };
        }
        if (prioritizeSection === 'done') {
          return {
            status: 'complete',
            dueDate: null,
            isLaterDate: false,
            prioritizeSection
          };
        }
        return {
          status: 'incomplete',
          dueDate: null,
          isLaterDate: false,
          prioritizeSection
        };
      };

      const prioritizeCreateOrigin = (prioritizeSection) =>
        prioritizeSection ? { prioritizeSection } : undefined;
      
      const caretPosition = getCurrentCaretPosition(e.target);
      const currentTextTrimmed = (e.target.textContent || e.target.innerText || '').trim();
      
      // Enter at caret position 0 AND task has text: create new task above/before the current task
      if (caretPosition === 0 && currentTextTrimmed.length > 0) {
        setEditingTaskId(null);
        e.target.blur();
        
        const siblings = tasks.filter(t => {
          if (currentTask.parent_task_id === null) {
            return t.parent_task_id === null;
          }
          return t.parent_task_id === currentTask.parent_task_id;
        });
        
        siblings.sort((a, b) => {
          const orderA = a.order !== null && a.order !== undefined ? a.order : Number.MAX_SAFE_INTEGER;
          const orderB = b.order !== null && b.order !== undefined ? b.order : Number.MAX_SAFE_INTEGER;
          if (orderA !== orderB) {
            return orderA - orderB;
          }
          return (a.created_at || '').localeCompare(b.created_at || '');
        });
        
        const currentIndex = siblings.findIndex(t => t._reactKey === clientId);
        const currentOrder = currentTask.order !== null && currentTask.order !== undefined
          ? currentTask.order
          : Number.MAX_SAFE_INTEGER;
        
        const newOrder = currentOrder;
        const orderUpdates = [];
        for (let i = currentIndex; i < siblings.length; i++) {
          const sibling = siblings[i];
          if (sibling.order !== null && sibling.order !== undefined) {
            orderUpdates.push({ id: sibling.id, order: sibling.order + 1 });
          }
        }
        // Apply order updates first (and await) so the server has the slot at newOrder free
        // before we create the new task. Otherwise auto-save can POST the new task while
        // the PATCH is still in flight, leaving two tasks with the same order; sort then
        // uses created_at and the new task appears below.
        if (orderUpdates.length > 0 && updateTaskOrders) {
          try {
            await updateTaskOrders(orderUpdates, true);
          } catch (err) {
            console.error('Error updating task orders:', err);
          }
        } else if (orderUpdates.length > 0) {
          orderUpdates.forEach(update => updateTaskOrder(update.id, update.order));
        }
        
        const createDefaults = getCreateDefaultsForCurrentTask();
        const newTask = createTask(
          '',
          currentTask.parent_task_id,
          newOrder,
          createDefaults.status,
          createDefaults.dueDate,
          null,
          createDefaults.isLaterDate,
          prioritizeCreateOrigin(createDefaults.prioritizeSection)
        );
        if (newTask) {
          setEditingTaskId(newTask._reactKey);
        }
        return;
      }
      
      if (e.shiftKey) {
        setEditingTaskId(null);
        e.target.blur();
        
        const siblings = tasks.filter(t => {
          if (currentTask.parent_task_id === null) {
            return t.parent_task_id === null;
          }
          return t.parent_task_id === currentTask.parent_task_id;
        });
        
        siblings.sort((a, b) => {
          const orderA = a.order !== null && a.order !== undefined ? a.order : Number.MAX_SAFE_INTEGER;
          const orderB = b.order !== null && b.order !== undefined ? b.order : Number.MAX_SAFE_INTEGER;
          if (orderA !== orderB) {
            return orderA - orderB;
          }
          return (a.created_at || '').localeCompare(b.created_at || '');
        });
        
        const currentIndex = siblings.findIndex(t => t._reactKey === clientId);
        const currentOrder = currentTask.order !== null && currentTask.order !== undefined
          ? currentTask.order
          : Number.MAX_SAFE_INTEGER;
        
        let newOrder = null;
        
        if (currentIndex > 0) {
          const previousTask = siblings[currentIndex - 1];
          const previousOrder = previousTask.order !== null && previousTask.order !== undefined
            ? previousTask.order
            : Number.MAX_SAFE_INTEGER;
          
          if (currentOrder - previousOrder > 1) {
            newOrder = currentOrder - 1;
          } else {
            newOrder = currentOrder;
            // Collect order updates for subsequent tasks
            const orderUpdates = [];
            for (let i = currentIndex; i < siblings.length; i++) {
              const sibling = siblings[i];
              if (sibling.order !== null && sibling.order !== undefined) {
                orderUpdates.push({ id: sibling.id, order: sibling.order + 1 });
              }
            }
            // Batch update all orders
            if (orderUpdates.length > 0 && updateTaskOrders) {
              updateTaskOrders(orderUpdates, true).catch(err => {
                console.error('Error updating task orders:', err);
              });
            } else if (orderUpdates.length > 0) {
              // Fallback to individual updates if batch not available
              orderUpdates.forEach(update => updateTaskOrder(update.id, update.order));
            }
          }
        } else {
          if (currentOrder !== Number.MAX_SAFE_INTEGER && currentOrder > 1) {
            newOrder = currentOrder - 1;
          } else {
            newOrder = currentOrder === Number.MAX_SAFE_INTEGER ? 1 : currentOrder;
            // Collect order updates for subsequent tasks
            const orderUpdates = [];
            for (let i = currentIndex; i < siblings.length; i++) {
              const sibling = siblings[i];
              if (sibling.order !== null && sibling.order !== undefined) {
                orderUpdates.push({ id: sibling.id, order: sibling.order + 1 });
              }
            }
            // Batch update all orders
            if (orderUpdates.length > 0 && updateTaskOrders) {
              updateTaskOrders(orderUpdates, true).catch(err => {
                console.error('Error updating task orders:', err);
              });
            } else if (orderUpdates.length > 0) {
              // Fallback to individual updates if batch not available
              orderUpdates.forEach(update => updateTaskOrder(update.id, update.order));
            }
          }
        }
        
        const createDefaults = getCreateDefaultsForCurrentTask();
        const newTask = createTask(
          '',
          currentTask.parent_task_id,
          newOrder,
          createDefaults.status,
          createDefaults.dueDate,
          null,
          createDefaults.isLaterDate,
          prioritizeCreateOrigin(createDefaults.prioritizeSection)
        );
        if (newTask) {
          // The task will be focused automatically after save completes via fetchTasks
          // Set editing state immediately so the UI shows it as editable
          setEditingTaskId(newTask._reactKey); // Use clientId
        }
        return;
      }
      
      const currentText = e.target.textContent || e.target.innerText;
      
      // Check if caret is not at the end (caretPosition already set above)
      const isCaretAtEnd = caretPosition >= currentText.length;
      
      let textBeforeCaret = currentText;
      let textAfterCaret = '';
      
      if (!isCaretAtEnd) {
        // Split text at caret position
        textBeforeCaret = currentText.substring(0, caretPosition);
        textAfterCaret = currentText.substring(caretPosition);
      }
      
      // Update current task with text before caret (preserve exact text, including trailing spaces)
      const trimmedTextBefore = textBeforeCaret.trim();
      
      // If caret is in the middle, we're splitting the task
      if (!isCaretAtEnd) {
        // Always update current task with text before caret (removes text after caret)
        const textToSave = trimmedTextBefore ? textBeforeCaret : '';
        // Update DOM element immediately to show the split
        e.target.textContent = textToSave;
        // Use textBeforeCaret (not trimmed) to preserve exact text before caret
        updateTaskText(currentTask.id, textToSave); // Use database ID
      } else {
        // Caret is at end - normal Enter behavior (create new task)
        if (trimmedTextBefore && trimmedTextBefore !== currentTask.text) {
          updateTaskText(currentTask.id, trimmedTextBefore); // Use database ID
        } else if (!trimmedTextBefore && currentTask.text !== '') {
          updateTaskText(currentTask.id, ''); // Use database ID
        }
      }
      
      setEditingTaskId(null);
      e.target.blur();

      // Prioritize view: new tasks from Enter at end are always root tasks (same as "Add task"
      // per section), not another subtask under the current parent.
      if (isCaretAtEnd && isPrioritizeView) {
        const createDefaults = getCreateDefaultsForCurrentTask();
        const initialText = (() => {
          const taskToCheck = { ...currentTask, text: trimmedTextBefore };
          if (isSequentialTask(taskToCheck)) {
            const currentSequenceNumber = getSequenceNumber(taskToCheck);
            if (currentSequenceNumber !== null) {
              return `${currentSequenceNumber + 1}.\u00A0`;
            }
          }
          return '';
        })();
        const rootTasks = tasks.filter(t => t.parent_task_id === null);
        const maxOrder = rootTasks.length > 0
          ? Math.max(...rootTasks.map(t => t.order !== null && t.order !== undefined ? t.order : 0))
          : 0;
        const newTask = createTask(
          initialText,
          null,
          maxOrder + 1,
          createDefaults.status,
          createDefaults.dueDate,
          null,
          createDefaults.isLaterDate,
          prioritizeCreateOrigin(createDefaults.prioritizeSection)
        );
        if (newTask) {
          setEditingTaskId(newTask._reactKey);
        }
        return;
      }
      
      const siblings = tasks.filter(t => {
        if (currentTask.parent_task_id === null) {
          return t.parent_task_id === null;
        }
        return t.parent_task_id === currentTask.parent_task_id;
      });
      
      siblings.sort((a, b) => {
        const orderA = a.order !== null && a.order !== undefined ? a.order : Number.MAX_SAFE_INTEGER;
        const orderB = b.order !== null && b.order !== undefined ? b.order : Number.MAX_SAFE_INTEGER;
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        return (a.created_at || '').localeCompare(b.created_at || '');
      });
      
      const currentIndex = siblings.findIndex(t => t._reactKey === clientId);
      let newOrder = null;
      let orderUpdates = [];
      
      if (currentIndex >= 0 && currentIndex < siblings.length - 1) {
        const nextTask = siblings[currentIndex + 1];
        const currentOrder = currentTask.order !== null && currentTask.order !== undefined ? currentTask.order : Number.MAX_SAFE_INTEGER;
        const nextOrder = nextTask.order !== null && nextTask.order !== undefined ? nextTask.order : Number.MAX_SAFE_INTEGER;
        
        if (nextOrder - currentOrder <= 1) {
          newOrder = currentOrder + 1;
          // Collect order updates for subsequent tasks
          for (let i = currentIndex + 1; i < siblings.length; i++) {
            const sibling = siblings[i];
            if (sibling.order !== null && sibling.order !== undefined) {
              orderUpdates.push({ id: sibling.id, order: sibling.order + 1 });
            }
          }
          // Batch update all orders
          if (orderUpdates.length > 0 && updateTaskOrders) {
            updateTaskOrders(orderUpdates, true).catch(err => {
              console.error('Error updating task orders:', err);
            });
          } else if (orderUpdates.length > 0) {
            // Fallback to individual updates if batch not available
            orderUpdates.forEach(update => updateTaskOrder(update.id, update.order));
          }
        } else {
          newOrder = currentOrder + 1;
        }
      } else {
        const maxOrder = siblings.length > 0 
          ? Math.max(...siblings.map(t => t.order !== null && t.order !== undefined ? t.order : 0))
          : 0;
        newOrder = maxOrder + 1;
      }
      
      // Check if current task is sequential, and if so, prepend next sequence number to new task
      // Use the trimmed text (which may have been edited) to check if it's sequential
      let newTaskText = textAfterCaret;
      const taskToCheck = { ...currentTask, text: trimmedTextBefore };
      if (isSequentialTask(taskToCheck) && isCaretAtEnd) {
        // Only add sequence number if caret was at end (normal behavior)
        const currentSequenceNumber = getSequenceNumber(taskToCheck);
        if (currentSequenceNumber !== null) {
          const nextSequenceNumber = currentSequenceNumber + 1;
          newTaskText = `${nextSequenceNumber}.\u00A0`;
        }
      }
      
      // Only create new task if there's text for it (or it's a sequential task with prefix)
      // When splitting, create new task if textAfterCaret exists (even if just whitespace)
      if (!isCaretAtEnd && textAfterCaret.length === 0) {
        // No text after caret, don't create new task
        return;
      }
      
      const createDefaults = getCreateDefaultsForCurrentTask();
      const newTask = createTask(
        newTaskText,
        currentTask.parent_task_id,
        newOrder,
        createDefaults.status,
        createDefaults.dueDate,
        null,
        createDefaults.isLaterDate,
        prioritizeCreateOrigin(createDefaults.prioritizeSection)
      );
      
      if (newTask) {
        // Build task list as it will be after creation (for renumbering)
        const orderUpdatesMap = new Map(orderUpdates.map(u => [u.id, u.order]));
        const tasksWithNewTask = tasks.map(t =>
          orderUpdatesMap.has(t.id) ? { ...t, order: orderUpdatesMap.get(t.id) } : t
        );
        tasksWithNewTask.push({
          ...newTask,
          order: newOrder,
          parent_task_id: currentTask.parent_task_id
        });

        const renumberUpdates = renumberSequentialListAroundTask(newTask, tasksWithNewTask);
        if (renumberUpdates.length > 0 && updateTaskOrders) {
          const batch = renumberUpdates.map(update => {
            const task = tasksWithNewTask.find(t => t.id === update.id);
            return { id: update.id, order: task ? task.order : undefined, text: update.text };
          });
          updateTaskOrders(batch, true).catch(err => {
            console.error('Error renumbering sequential tasks after create:', err);
          });
        }

        // Check if the new task text is just a sequential prefix (e.g., "2. ")
        // This happens when creating a new sequential task - the text is just the prefix
        const isNewTaskJustPrefix = isSequentialTask({ text: newTaskText }) && 
          newTaskText.trim().match(/^\d+\.\s*$/) !== null;
        
        // If we split text, mark the new task to have caret at position zero
        // BUT: don't set caret at zero for sequential tasks that are just the prefix
        // (they should have caret at the end, after the prefix)
        if (!isCaretAtEnd && newTask._reactKey && caretAtZeroTaskIdsRef && !isNewTaskJustPrefix) {
          caretAtZeroTaskIdsRef.current.add(newTask._reactKey); // Use clientId
        }
        
        // Explicitly ensure sequential tasks with just the prefix have caret at end
        // (not at position 0, which would be before the prefix)
        if (isNewTaskJustPrefix && newTask._reactKey && caretAtZeroTaskIdsRef) {
          caretAtZeroTaskIdsRef.current.delete(newTask._reactKey); // Use clientId
        }
        
        // The task will be focused automatically after save completes via fetchTasks
        // Set editing state immediately so the UI shows it as editable
        setEditingTaskId(newTask._reactKey); // Use clientId
      }
    } else if (e.key === ' ') {
      // On mobile (no Tab key): Space at caret 0 = Tab (make task a subtask)
      const currentTaskForSpace = tasks.find(t => t._reactKey === clientId);
      if (currentTaskForSpace && isMobile && getCurrentCaretPosition(e.target) === 0) {
        e.preventDefault();
        return handleTaskTextKeyDown(Object.assign({}, e, { key: 'Tab', shiftKey: false, preventDefault: () => {} }), clientId);
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      
      const currentTask = tasks.find(t => t._reactKey === clientId);
      if (!currentTask) return;
      
      let currentText = '';
      if (e.target.textContent !== null && e.target.textContent !== undefined) {
        currentText = e.target.textContent;
      } else if (e.target.innerText !== null && e.target.innerText !== undefined) {
        currentText = e.target.innerText;
      } else {
        currentText = currentTask.text || '';
      }
      
      // Check if task is sequential and remove "X. " prefix when subtask status changes
      let savedText = currentText;
      let prefixRemoved = false;
      // Track if we add a prefix during un-tabbing (needed for caret position adjustment)
      let prefixAddedLength = 0;
      
      const taskToCheck = { ...currentTask, text: currentText };
      if (isSequentialTask(taskToCheck)) {
        // Remove the sequential prefix (e.g., "1. " or "2. ")
        // Match pattern at start (after optional leading whitespace)
        const beforeLength = savedText.length;
        savedText = currentText.replace(/^(\s*)\d+\.\s+/, '$1');
        prefixRemoved = savedText.length < beforeLength;
      }
      
      // Save caret position before the operation
      const caretPosition = getCurrentCaretPosition(e.target);
      
      if (e.shiftKey) {
        if (currentTask.parent_task_id) {
          const parentTask = tasks.find(t => t.id === currentTask.parent_task_id);
          if (parentTask) {
            let newParentId = null;
            if (parentTask.parent_task_id !== null && parentTask.parent_task_id !== undefined) {
              newParentId = parentTask.parent_task_id;
            }
            
            let newOrder = null;
            
            const newSiblings = tasks.filter(t => {
              if (newParentId === null) {
                return t.parent_task_id === null;
              }
              return t.parent_task_id === newParentId;
            });
            
            newSiblings.sort((a, b) => {
              const orderA = a.order !== null && a.order !== undefined ? a.order : Number.MAX_SAFE_INTEGER;
              const orderB = b.order !== null && b.order !== undefined ? b.order : Number.MAX_SAFE_INTEGER;
              if (orderA !== orderB) {
                return orderA - orderB;
              }
              return (a.created_at || '').localeCompare(b.created_at || '');
            });
            
            const parentIndex = newSiblings.findIndex(t => t.id === parentTask.id);
            
            if (parentIndex >= 0 && parentIndex < newSiblings.length - 1) {
              const nextSibling = newSiblings[parentIndex + 1];
              const parentOrder = parentTask.order !== null && parentTask.order !== undefined ? parentTask.order : Number.MAX_SAFE_INTEGER;
              const nextOrder = nextSibling.order !== null && nextSibling.order !== undefined ? nextSibling.order : Number.MAX_SAFE_INTEGER;
              
              if (nextOrder - parentOrder <= 1) {
                newOrder = parentOrder + 1;
                // Collect order updates for subsequent tasks
                const orderUpdates = [];
                for (let i = parentIndex + 1; i < newSiblings.length; i++) {
                  const sibling = newSiblings[i];
                  if (sibling.order !== null && sibling.order !== undefined) {
                    orderUpdates.push({ id: sibling.id, order: sibling.order + 1 });
                  }
                }
                // Batch update all orders
                if (orderUpdates.length > 0 && updateTaskOrders) {
                  updateTaskOrders(orderUpdates, true).catch(err => {
                    console.error('Error updating task orders:', err);
                  });
                } else if (orderUpdates.length > 0) {
                  // Fallback to individual updates if batch not available
                  orderUpdates.forEach(update => updateTaskOrder(update.id, update.order));
                }
              } else {
                newOrder = parentOrder + 1;
              }
            } else {
              const maxOrder = newSiblings.length > 0 
                ? Math.max(...newSiblings.map(t => t.order !== null && t.order !== undefined ? t.order : 0))
                : 0;
              newOrder = maxOrder + 1;
            }
            
            // If the task before the promoted task on the new level has a number and dot (e.g. 3. c),
            // prepend (that number + 1) to the promoted task (e.g. 4. a).
            const taskToCheckForSequential = { ...currentTask, text: savedText };
            const newSiblingsSorted = [...newSiblings].sort((a, b) => {
              const orderA = a.order !== null && a.order !== undefined ? a.order : Number.MAX_SAFE_INTEGER;
              const orderB = b.order !== null && b.order !== undefined ? b.order : Number.MAX_SAFE_INTEGER;
              if (orderA !== orderB) return orderA - orderB;
              return (a.created_at || '').localeCompare(b.created_at || '');
            });
            let previousTaskOnNewLevel = null;
            for (let i = newSiblingsSorted.length - 1; i >= 0; i--) {
              const o = newSiblingsSorted[i].order !== null && newSiblingsSorted[i].order !== undefined
                ? newSiblingsSorted[i].order
                : Number.MAX_SAFE_INTEGER;
              if (o < newOrder) {
                previousTaskOnNewLevel = newSiblingsSorted[i];
                break;
              }
            }
            if (!isSequentialTask(taskToCheckForSequential) && previousTaskOnNewLevel && isSequentialTask(previousTaskOnNewLevel)) {
              const prevNum = getSequenceNumber(previousTaskOnNewLevel);
              if (prevNum !== null) {
                const sequenceNumber = prevNum + 1;
                const leadingWhitespace = savedText.match(/^(\s*)/)?.[1] || '';
                const prefix = `${sequenceNumber}. `;
                savedText = leadingWhitespace + prefix + savedText.trim();
                prefixAddedLength = prefix.length;
              }
            }

            // If we still have no sequential prefix but will be first among sequential siblings (e.g. outdented 1. rejoining 2., 3., 4.), add "1. " so renumbering runs correctly.
            if (!isSequentialTask({ ...currentTask, text: savedText })) {
              const nextSiblingOnNewLevel = parentIndex >= 0 && parentIndex + 1 < newSiblings.length ? newSiblings[parentIndex + 1] : null;
              if (nextSiblingOnNewLevel && isSequentialTask(nextSiblingOnNewLevel)) {
                const leadingWhitespace = savedText.match(/^(\s*)/)?.[1] || '';
                const prefix = '1. ';
                savedText = leadingWhitespace + prefix + savedText.trim();
                prefixAddedLength = prefix.length;
              }
            }

            // Update the moved task
            updateTaskParentWithTextAndOrder(currentTask.id, newParentId, savedText, newOrder);

            // Run unified renumbering so the list is correct (e.g. fix gaps or wrong numbers)
            const promotedTask = { ...currentTask, parent_task_id: newParentId, order: newOrder, text: savedText };
            const tasksAfterPromotion = tasks.map(t =>
              t.id === currentTask.id ? { ...t, parent_task_id: newParentId, order: newOrder, text: savedText } : t
            );
            const renumberUpdates = renumberSequentialListAroundTask(promotedTask, tasksAfterPromotion);
            if (renumberUpdates.length > 0 && updateTaskOrders) {
              try {
                const batch = renumberUpdates.map(update => {
                  const task = tasksAfterPromotion.find(t => t.id === update.id);
                  return { id: update.id, order: task ? task.order : undefined, text: update.text };
                });
                await updateTaskOrders(batch, true);
              } catch (err) {
                console.error('Error renumbering sequential tasks after shift-tab:', err);
              }
            }
          }
        }
      } else {
        const previousSibling = findPreviousSibling(currentTask.id, tasks);
        
        if (previousSibling) {
          const existingSubtasks = tasks.filter(t => t.parent_task_id === previousSibling.id);
          
          existingSubtasks.sort((a, b) => {
            const orderA = a.order !== null && a.order !== undefined ? a.order : Number.MAX_SAFE_INTEGER;
            const orderB = b.order !== null && b.order !== undefined ? b.order : Number.MAX_SAFE_INTEGER;
            if (orderA !== orderB) {
              return orderA - orderB;
            }
            return (a.created_at || '').localeCompare(b.created_at || '');
          });
          
          let newOrder = 1;
          if (existingSubtasks.length > 0) {
            const maxOrder = Math.max(...existingSubtasks.map(t => t.order !== null && t.order !== undefined ? t.order : 0));
            newOrder = maxOrder + 1;
          }
          
          updateTaskParentWithTextAndOrder(currentTask.id, previousSibling.id, savedText, newOrder);

          // Renumber remaining sequential siblings (e.g. 3., 4. → 2., 3. after indenting 2.)
          const tasksAfterIndent = tasks.map(t =>
            t.id === currentTask.id ? { ...t, parent_task_id: previousSibling.id, order: newOrder, text: savedText } : t
          );
          const renumberUpdates = renumberSequentialListAfterIndent(previousSibling, currentTask.parent_task_id, tasksAfterIndent);
          if (renumberUpdates.length > 0 && updateTaskOrders) {
            const batch = renumberUpdates.map(update => {
              const task = tasksAfterIndent.find(t => t.id === update.id);
              return { id: update.id, order: task ? task.order : undefined, text: update.text };
            });
            updateTaskOrders(batch, true).catch(err => {
              console.error('Error renumbering sequential tasks after indent:', err);
            });
          }

          // Use clientId for expandedTasks
          const previousSiblingClientId = previousSibling._reactKey;
          if (!expandedTasks.has(previousSiblingClientId)) {
            setExpandedTasks(prev => new Set(prev).add(previousSiblingClientId));
          }
          
          // Ensure parent task status is 'expanded' when it gets a subtask
          if (previousSibling.status !== 'expanded') {
            updateTaskStatus(previousSibling.id, 'expanded');
          }
        }
      }
      
      const runFocus = (element) => {
        if (element.textContent !== savedText) {
          element.textContent = savedText;
        }
        setEditingTaskId(clientId);
        editingElementRef.current = element;
        element.focus();

        const targetText = element.textContent || '';
        const targetTextLength = targetText.length;
        let adjustedCaretPosition = caretPosition;
        if (prefixAddedLength > 0) {
          adjustedCaretPosition = caretPosition + prefixAddedLength;
        } else if (prefixRemoved && caretPosition > 0) {
          const removedLength = currentText.length - savedText.length;
          adjustedCaretPosition = Math.max(0, caretPosition - removedLength);
        }
        const targetCaretPosition = Math.min(adjustedCaretPosition, targetTextLength);

        const newRange = document.createRange();
        if (targetTextLength > 0) {
          const walker = document.createTreeWalker(
            element,
            NodeFilter.SHOW_TEXT,
            null
          );
          let currentOffset = 0;
          let textNode = walker.nextNode();
          let targetNode = null;
          while (textNode) {
            const nodeLength = textNode.textContent.length;
            if (currentOffset + nodeLength >= targetCaretPosition) {
              targetNode = textNode;
              break;
            }
            currentOffset += nodeLength;
            textNode = walker.nextNode();
          }
          if (targetNode) {
            const targetOffset = targetCaretPosition - currentOffset;
            newRange.setStart(targetNode, targetOffset);
            newRange.collapse(true);
          } else {
            newRange.selectNodeContents(element);
            newRange.collapse(false);
          }
        } else {
          newRange.setStart(element, 0);
          newRange.collapse(true);
        }
        const newSelection = window.getSelection();
        newSelection.removeAllRanges();
        newSelection.addRange(newRange);
      };

      if (pendingFocusRef) {
        pendingFocusRef.current = { reactKey: clientId, runFocus };
        setFocusRequestId((n) => n + 1);
      }
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();

      // Handle ArrowUp on placeholder - focus filename field
      const isPlaceholder = clientId === 'placeholder_client_key' || clientId === 'placeholder';
      if (e.key === 'ArrowUp' && isPlaceholder && focusFileName) {
        setEditingTaskId(null);
        e.target.blur();
        focusFileName();
        return;
      }

      const currentText = e.target.textContent || e.target.innerText;
      const trimmedText = currentText.trim();
      const currentTask = tasks.find(t => t._reactKey === clientId);

      if (currentTask && trimmedText && trimmedText !== currentTask.text) {
        updateTaskText(currentTask.id, trimmedText); // Use database ID
      }

      const taskTree = buildTaskTree(tasks);
      const flattenedTasks = flattenTaskTree(taskTree, expandedTasks);

      const currentIndex = flattenedTasks.findIndex(t => t._reactKey === clientId);

      if (currentIndex >= 0) {
        // Handle ArrowUp on first task - focus filename field
        if (e.key === 'ArrowUp' && currentIndex === 0 && focusFileName) {
          // Save current task text if changed
          const currentText = e.target.textContent || e.target.innerText;
          const trimmedText = currentText.trim();
          const currentTask = tasks.find(t => t._reactKey === clientId);
          
          if (currentTask && trimmedText && trimmedText !== currentTask.text) {
            updateTaskText(currentTask.id, trimmedText); // Use database ID
          }
          
          // Blur current task and focus filename
          setEditingTaskId(null);
          e.target.blur();
          focusFileName();
          return;
        }
        
        let targetTask = null;
        
        if (e.key === 'ArrowUp' && currentIndex > 0) {
          targetTask = flattenedTasks[currentIndex - 1];
        } else if (e.key === 'ArrowDown' && currentIndex < flattenedTasks.length - 1) {
          targetTask = flattenedTasks[currentIndex + 1];
        }
        
        if (targetTask) {
          const caretPosition = getCurrentCaretPosition(e.target);
          
          setEditingTaskId(null);
          e.target.blur();
          
          setEditingTaskId(targetTask._reactKey); // Use clientId
          // Store caret positioning data for useLayoutEffect to handle after React renders
          tabCaretPositionRef.current = {
            taskId: targetTask._reactKey, // Use clientId
            caretPosition
          };
        }
      }
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      // Handle expand/collapse with arrow keys when caret is at first/last position
      const currentTask = tasks.find(t => t._reactKey === clientId);
      if (!currentTask) return;
      
      // Check if task has subtasks
      const hasSubtasks = tasks.some(t => t.parent_task_id === currentTask.id); // Use database ID
      if (!hasSubtasks) return;
      
      // Get caret position
      const caretPosition = getCurrentCaretPosition(e.target);
      const currentText = e.target.textContent || e.target.innerText || '';
      
      const isCaretAtStart = caretPosition === 0;
      const isCaretAtEnd = caretPosition >= currentText.length;
      const isCurrentlyExpanded = expandedTasks.has(clientId); // Use clientId
      
      if (e.key === 'ArrowRight') {
        // Right arrow at end: expand if collapsed
        if (isCaretAtEnd && !isCurrentlyExpanded) {
          e.preventDefault();
          setExpandedTasks(prev => {
            const newSet = new Set(prev);
            newSet.add(clientId); // Use clientId
            return newSet;
          });
          updateTaskStatus(currentTask.id, 'expanded'); // Use database ID
        }
      } else if (e.key === 'ArrowLeft') {
        // Left arrow at start: collapse if expanded
        if (isCaretAtStart && isCurrentlyExpanded) {
          e.preventDefault();
          setExpandedTasks(prev => {
            const newSet = new Set(prev);
            newSet.delete(clientId); // Use clientId
            return newSet;
          });
          updateTaskStatus(currentTask.id, 'collapsed'); // Use database ID
        }
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditingTaskId(null);
      const originalTask = tasks.find(t => t._reactKey === clientId);
      if (originalTask) {
        e.target.textContent = originalTask.text;
      }
      e.target.blur();
    }
  }, [
    tasks,
    expandedTasks,
    setEditingTaskId,
    setExpandedTasks,
    editingElementRef,
    updateTaskText,
    updateTaskStatus,
    updateTaskOrder,
    updateTaskOrders,
    updateTaskParentWithTextAndOrder,
    createTask,
    deleteTask,
    caretAtZeroTaskIdsRef,
    focusFileName,
    arrowKeyCaretPositionRef,
    tabCaretPositionRef,
    pendingFocusRef,
    filter,
    visiblePrioritizeSections
  ]);

  return {
    handleTaskTextKeyDown,
    tryFocusPending,
    focusRequestId
  };
}
